import {
  Button,
  ControlGroup,
  Divider,
  Select,
  SelectItem,
  Small,
  styled,
  Text,
  TextInput,
  useViewport,
} from '@meterup/atto';
import { useEffect, useMemo, useState } from 'react';

import type { SharedColumnDef } from '../createColumnBuilder';
import type { AutoCollectionStateInterface } from '../utils';

const TablePaginationDivider = styled(Divider, {
  padding: '$6 0',
});
TablePaginationDivider.displayName = 'TablePaginationDivider';

const TablePaginationControls = styled(ControlGroup);
TablePaginationControls.displayName = 'TablePaginationControls';

const TablePaginationContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$12',
  width: '100%',
});
TablePaginationContainer.displayName = 'TablePaginationContainer';

function PrevPageButton({
  onClick,
  disabled,
  breakpoint,
}: {
  onClick: () => void;
  disabled: boolean;
  breakpoint: string;
}) {
  return (
    <Button
      onClick={onClick}
      arrangement="hidden-label"
      disabled={disabled}
      icon="arrow-left"
      variant="secondary"
      width={breakpoint !== 'mobile' ? 'auto' : '100%'}
    >
      Previous page
    </Button>
  );
}

function NextPageButton({
  onClick,
  disabled,
  breakpoint,
}: {
  onClick: () => void;
  disabled: boolean;
  breakpoint: string;
}) {
  return (
    <Button
      onClick={onClick}
      arrangement="hidden-label"
      disabled={disabled}
      icon="arrow-right"
      variant="secondary"
      width={breakpoint !== 'mobile' ? 'auto' : '100%'}
    >
      Next page
    </Button>
  );
}

export function TablePagination({
  offset,
  setOffset,
  limit,
  total,
  numEntriesOnPage,
  objectLabel,
}: {
  offset: number;
  setOffset: (value: number) => void;
  limit: number;
  total: number;
  numEntriesOnPage: number;
  objectLabel?: React.ReactNode;
}) {
  const { breakpoint } = useViewport();
  const totalPages = total ? Math.ceil(total / limit) : 1;
  const currentPage = Math.floor(offset / limit) + 1;

  const hasMore = useMemo(
    () => offset + numEntriesOnPage < total,
    [numEntriesOnPage, total, offset],
  );

  const handlePageChange = (newPage: string) => {
    const pageNum = Number(newPage);
    if (!Number.isNaN(pageNum)) {
      setOffset((pageNum - 1) * limit);
    }
  };

  const pageCounts = useMemo(() => {
    const PAGES_TO_SHOW = 40;
    const halfPages = Math.floor(PAGES_TO_SHOW / 2);
    let startPage = Math.max(1, currentPage - halfPages);
    const endPage = Math.min(totalPages, startPage + PAGES_TO_SHOW - 1);

    if (endPage - startPage + 1 < PAGES_TO_SHOW) {
      startPage = Math.max(1, endPage - PAGES_TO_SHOW + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i += 1) {
      pages.push({
        key: String(i),
        value: String(i),
      });
    }

    if (startPage > 1) {
      pages.unshift({
        key: '1',
        value: '1',
      });
      if (startPage > 2) {
        pages.splice(1, 0, {
          key: '...',
          value: '...',
        });
      }
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push({
          key: '...',
          value: '...',
        });
      }
      pages.push({
        key: String(totalPages),
        value: String(totalPages),
      });
    }

    return pages;
  }, [currentPage, totalPages]);

  const viewingStart = Math.min(offset + 1, total);
  const viewingEnd = Math.min(offset + numEntriesOnPage, total);

  return (
    <TablePaginationContainer>
      <TablePaginationControls relation="separate" size="small">
        <PrevPageButton
          onClick={() => setOffset(offset - limit)}
          disabled={currentPage <= 1}
          breakpoint={breakpoint}
        />
        <Select
          value={String(currentPage)}
          onValueChange={handlePageChange}
          items={pageCounts}
          aria-label="Select page"
          width={breakpoint !== 'mobile' ? '4rem' : '100%'}
        >
          {({ key, value }) => <SelectItem key={key}>{value}</SelectItem>}
        </Select>
        <Small whitespace="no-wrap">
          of <Text weight="bold">{totalPages}</Text>
        </Small>
        <NextPageButton
          onClick={() => setOffset(offset + limit)}
          disabled={!hasMore || currentPage >= totalPages}
          breakpoint={breakpoint}
        />
      </TablePaginationControls>
      <TablePaginationDivider appearance="thick" direction="column" />
      <Small whitespace="no-wrap">
        <Text weight="bold">
          {viewingStart} – {viewingEnd}
        </Text>{' '}
        of <Text weight="bold">{total}</Text> {objectLabel ?? 'items'}
      </Small>
    </TablePaginationContainer>
  );
}

const PAGE_THRESHOLD_FOR_TEXT_INPUT = 40;

// Enhanced component that works with TableState
export function TablePaginationEnhanced<
  D extends any = any,
  C extends SharedColumnDef<D, any> = SharedColumnDef<D, any>,
>({
  tableState,
  label,
  showTotal = true,
}: {
  tableState: Pick<
    AutoCollectionStateInterface<D, C['id']>,
    'tableRef' | 'limit' | 'offset' | 'numRows' | 'setOffset'
  >;
  label?: React.ReactNode;
  showTotal?: boolean;
}) {
  const total = tableState.numRows || tableState.tableRef.current?.filteredRows.length || 0;
  const { breakpoint } = useViewport();
  const totalPages = total ? Math.ceil(total / tableState.limit) : 1;
  const currentPage = Math.floor(tableState.offset / tableState.limit) + 1;
  const numEntriesOnPage = Math.min(Math.max(0, total - tableState.offset), tableState.limit);

  const [pageInputValue, setPageInputValue] = useState(String(currentPage));

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  const hasMore = useMemo(
    () => tableState.offset + numEntriesOnPage < total,
    [numEntriesOnPage, total, tableState.offset],
  );

  const handlePageChange = (newPage: string) => {
    const pageNum = Number(newPage);
    if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      tableState.setOffset((pageNum - 1) * tableState.limit);
    }
  };

  const handlePageInputSubmit = () => {
    const pageNum = Number(pageInputValue);
    if (Number.isNaN(pageNum) || pageInputValue === '') {
      setPageInputValue(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(pageNum, 1), totalPages);
    setPageInputValue(String(clamped));
    tableState.setOffset((clamped - 1) * tableState.limit);
  };

  const usesTextInput = totalPages >= PAGE_THRESHOLD_FOR_TEXT_INPUT;

  const pageCounts = useMemo(() => {
    if (usesTextInput) return [];
    const pages = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push({
        key: String(i),
        value: String(i),
      });
    }
    return pages;
  }, [totalPages, usesTextInput]);

  const viewingStart = Math.min(tableState.offset + 1, total);
  const viewingEnd = Math.min(tableState.offset + numEntriesOnPage, total);

  return (
    <TablePaginationContainer>
      <TablePaginationControls relation="separate" size="small">
        <PrevPageButton
          onClick={() => tableState.setOffset(tableState.offset - tableState.limit)}
          disabled={currentPage <= 1}
          breakpoint={breakpoint}
        />
        {usesTextInput ? (
          <TextInput
            type="number"
            inputProps={{ min: 1, max: totalPages }}
            value={pageInputValue}
            onChange={(val) => setPageInputValue(val ?? '')}
            onBlur={handlePageInputSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePageInputSubmit();
              }
            }}
            aria-label="Go to page"
            width={breakpoint !== 'mobile' ? '4rem' : '100%'}
          />
        ) : (
          <Select
            value={String(currentPage)}
            onValueChange={handlePageChange}
            items={pageCounts}
            aria-label="Select page"
            width={breakpoint !== 'mobile' ? '4rem' : '100%'}
          >
            {({ key, value }) => <SelectItem key={key}>{value}</SelectItem>}
          </Select>
        )}
        <Small whitespace="no-wrap">
          of <Text weight="bold">{totalPages}</Text>
        </Small>
        <NextPageButton
          onClick={() => tableState.setOffset(tableState.offset + tableState.limit)}
          disabled={!hasMore || currentPage >= totalPages}
          breakpoint={breakpoint}
        />
      </TablePaginationControls>
      <TablePaginationDivider appearance="thick" direction="column" />
      <Small whitespace="no-wrap">
        <Text weight="bold">
          {viewingStart} – {viewingEnd}
        </Text>{' '}
        {showTotal && (
          <>
            of <Text weight="bold">{total}</Text>{' '}
          </>
        )}
        {label ?? 'items'}
      </Small>
    </TablePaginationContainer>
  );
}
